import React, { useState, useEffect } from 'react';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import posthog from 'posthog-js';
import CertificateStats from './CertificateStats';
import CertificateList from './CertificateList';
import CertificateModal from './CertificateModal';
import CertificatePreviewModal from './CertificatePreviewModal';
import EmailModal from './EmailModal';
import ProfileModal from './ProfileModal';
import ProfileDropdown from './ProfileDropdown';
import MyProfileModal from './MyProfileModal';
import NotificationModal from './NotificationModal';
import AccountModal from './AccountModal';
import Toast from './Toast';
import { certificateAPI, userAPI, authAPI } from '../services/api';
import '../styles/Dashboard.css';

function Dashboard() {
  const { logout, isAuthenticated, isLoading, user, getToken } = useKindeAuth();

  const [certificates, setCertificates] = useState([]);
  const [stats, setStats] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCertificate, setEditingCertificate] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewCertificate, setPreviewCertificate] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isMyProfileModalOpen, setIsMyProfileModalOpen] = useState(false);
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const [isAccountModalOpen, setIsAccountModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  // Show toast notification
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  // Initialize user and fetch data after authentication
  useEffect(() => {
    if (isAuthenticated) {
      initializeUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const initializeUser = async () => {
    try {
      // Get Kinde token
      const token = await getToken();
      if (token) {
        // Store token for API calls
        localStorage.setItem('kinde_token', token);
        
        // Get user info from Kinde SDK (user is already an object, not a promise)
        // Note: user object from Kinde SDK contains: id, email, given_name, family_name, picture
        console.log('Kinde user object:', user);
        
        // Verify user with backend, sending full user info
        const verifyResult = await authAPI.verify({
          token,
          email: user?.email,
          given_name: user?.given_name,
          family_name: user?.family_name
        });
        
        console.log('Verify result:', verifyResult);
        setCurrentUser(verifyResult.user);
        
        // Identify user in PostHog
        posthog.identify(verifyResult.user.id, {
          email: verifyResult.user.email,
          name: verifyResult.user.name
        });
        
        // Check if user needs to complete profile (name is missing or generic)
        if (!verifyResult.user.name || verifyResult.user.name === 'User' || verifyResult.user.name.includes('@')) {
          setIsMyProfileModalOpen(true);
        }
      }
      
      // Fetch certificates and stats
      await fetchCertificates();
      await fetchStats();
    } catch (err) {
      console.error('Error initializing user:', err);
      setError('Failed to initialize user session');
    }
  };

  const fetchCertificates = async () => {
    try {
      setLoading(true);
      const data = await certificateAPI.getAll();
      // Ensure we have an array
      const certsArray = Array.isArray(data) ? data : [];
      setCertificates(certsArray);
      
      // Track user's current certificate inventory
      if (certsArray.length > 0) {
        // Count certificates by type
        const certsByType = certsArray.reduce((acc, cert) => {
          const type = cert.certificate_type || 'Unknown';
          acc[type] = (acc[type] || 0) + 1;
          return acc;
        }, {});
        
        // Track inventory stats
        posthog.capture('certificate_inventory_loaded', {
          total_certificates: certsArray.length,
          certificates_by_type: certsByType,
          has_expired: certsArray.some(c => c.status === 'expired'),
          expiring_soon_count: certsArray.filter(c => c.status === 'expiring_soon').length
        });
        
        // Set user properties for segmentation
        posthog.setPersonProperties({
          certificate_count: certsArray.length,
          certificate_types: Object.keys(certsByType),
          most_common_cert_type: Object.entries(certsByType).sort((a, b) => b[1] - a[1])[0]?.[0]
        });
      }
      
      setError(null);
    } catch (err) {
      console.error('Error fetching certificates:', err);
      setCertificates([]);
      setError('Failed to load certificates');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await userAPI.getStats();
      setStats(data.stats);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  // Open modal for adding new certificate
  const handleAddNew = () => {
    setEditingCertificate(null);
    setIsModalOpen(true);
  };

  // Open modal for editing existing certificate
  const handleEdit = (certificate) => {
    setEditingCertificate(certificate);
    setIsModalOpen(true);
  };

  // Close modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingCertificate(null);
  };

  // Save certificate (create or update)
  const handleSave = async (formData, certificateId) => {
    try {
      setLoading(true);
      if (certificateId) {
        // Update existing
        await certificateAPI.update(certificateId, formData);
        showToast('Certificate updated successfully');
        
        // Track certificate update
        posthog.capture('certificate_updated', {
          certificate_type: formData.get('certificate_type')
        });
      } else {
        // Create new
        await certificateAPI.create(formData);
        showToast('Certificate added successfully');
        
        // Track certificate upload
        posthog.capture('certificate_uploaded', {
          certificate_type: formData.get('certificate_type'),
          has_file: formData.has('file'),
          used_ocr: formData.get('used_ocr') === 'true'
        });
      }
      
      // Refresh data
      await fetchCertificates();
      await fetchStats();
      
      handleCloseModal();
      setError(null);
    } catch (err) {
      console.error('Error saving certificate:', err);
      const errorMessage = err.response?.data?.message || 'Failed to save certificate';
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Delete certificate
  const handleDelete = async (certificateId) => {
    try {
      setLoading(true);
      
      // Get certificate details before deleting (for tracking)
      const certificate = certificates.find(c => c.id === certificateId);
      
      await certificateAPI.delete(certificateId);
      
      showToast('Certificate deleted');
      
      // Track certificate deletion
      if (certificate) {
        posthog.capture('certificate_deleted', {
          certificate_type: certificate.certificate_type,
          had_file: !!certificate.file_url
        });
      }
      
      // Refresh data
      await fetchCertificates();
      await fetchStats();
      
      setError(null);
    } catch (err) {
      console.error('Error deleting certificate:', err);
      showToast('Failed to delete certificate', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Download certificate file
  const handleDownload = async (certificateId) => {
    try {
      const data = await certificateAPI.getDownloadUrl(certificateId);
      if (data.url) {
        window.open(data.url, '_blank');
      }
    } catch (err) {
      console.error('Error downloading certificate:', err);
      alert('Failed to download certificate');
    }
  };

  // Preview certificate file
  const handlePreview = (certificate) => {
    setPreviewCertificate(certificate);
    setIsPreviewOpen(true);
  };

  // Close preview modal
  const handleClosePreview = () => {
    setIsPreviewOpen(false);
    setPreviewCertificate(null);
  };

  // Toggle selection
  const handleToggleSelection = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  // Toggle select all
  const handleSelectAll = () => {
    if (selectedIds.size === certificates.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(certificates.map((c) => c.id)));
    }
  };

  // Open email modal
  const handleEmail = () => {
    setIsEmailModalOpen(true);
  };

  // Close email modal
  const handleCloseEmailModal = () => {
    setIsEmailModalOpen(false);
  };

  // Send email
  const handleSendEmail = async (recipientEmail, message) => {
    try {
      const selectedCertIds = Array.from(selectedIds);
      
      await certificateAPI.emailCertificates(selectedCertIds, recipientEmail, message);
      
      // Track certificate sharing
      posthog.capture('certificates_shared_via_email', {
        certificate_count: selectedCertIds.length,
        has_message: message && message.length > 0
      });
      
      // Don't show toast - success message shown in modal
      setSelectedIds(new Set()); // Clear selection
    } catch (err) {
      console.error('Email send error:', err);
      showToast('Failed to send certificates', 'error');
      throw err;
    }
  };

  // Save profile
  const handleSaveProfile = async (profileData) => {
    try {
      console.log('Saving profile with data:', profileData);
      const result = await authAPI.updateProfile(profileData);
      console.log('Profile save result:', result);
      setCurrentUser(result.user);
      showToast('Profile updated successfully');
      
      // Track profile completion
      posthog.capture('profile_updated', {
        has_name: !!profileData.name,
        notification_preferences_set: !!profileData.notificationPreferences
      });
    } catch (err) {
      console.error('Profile save error:', err);
      showToast('Failed to save profile', 'error');
      throw err;
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="loading-container">
        Loading...
      </div>
    );
  }

  // Authenticated - show dashboard
  return (
    <div>
      <header className="header">
        <div className="header-content">
          <h1>Certifiable</h1>
          <div className="header-right">
            <ProfileDropdown 
              currentUser={currentUser}
              onOpenProfile={() => setIsMyProfileModalOpen(true)}
              onOpenNotifications={() => setIsNotificationModalOpen(true)}
              onOpenAccount={() => setIsAccountModalOpen(true)}
              onLogout={logout}
            />
            <button onClick={logout} className="btn-logout">
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="page-wrap">
        {error && (
          <div style={{
            background: '#fef2f2',
            color: '#991b1b',
            padding: '15px',
            borderRadius: '12px',
            marginBottom: '20px',
            border: '1px solid #fecaca',
          }}>
            {error}
          </div>
        )}

        <div className="section">
          <CertificateStats stats={stats} />
        </div>

        <div className="section" style={{ marginBottom: '20px' }}>
          <button className="btn btn-primary" onClick={handleAddNew} disabled={loading}>
            ➕ Add Certificate
          </button>
        </div>

        <CertificateList
          certificates={certificates}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onDownload={handleDownload}
          onPreview={handlePreview}
          selectedIds={selectedIds}
          onToggleSelection={handleToggleSelection}
          onSelectAll={handleSelectAll}
          onEmail={handleEmail}
        />

        <CertificateModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onSave={handleSave}
          certificate={editingCertificate}
        />

        <CertificatePreviewModal
          isOpen={isPreviewOpen}
          onClose={handleClosePreview}
          certificate={previewCertificate}
        />

        <EmailModal
          isOpen={isEmailModalOpen}
          onClose={handleCloseEmailModal}
          selectedCertificates={certificates.filter(c => selectedIds.has(c.id))}
          onSend={handleSendEmail}
        />

        <ProfileModal
          isOpen={isProfileModalOpen}
          onClose={() => setIsProfileModalOpen(false)}
          onSave={handleSaveProfile}
          currentUser={currentUser}
          onExportSuccess={() => showToast('Your data has been exported successfully! Check your downloads folder.')}
          onExportError={() => showToast('Failed to export data. Please try again.', 'error')}
        />

        <MyProfileModal
          isOpen={isMyProfileModalOpen}
          onClose={() => setIsMyProfileModalOpen(false)}
          onSave={handleSaveProfile}
          currentUser={currentUser}
        />

        <NotificationModal
          isOpen={isNotificationModalOpen}
          onClose={() => setIsNotificationModalOpen(false)}
          onSave={handleSaveProfile}
          currentUser={currentUser}
        />

        <AccountModal
          isOpen={isAccountModalOpen}
          onClose={() => setIsAccountModalOpen(false)}
          onExportSuccess={() => showToast('Your data has been exported successfully! Check your downloads folder.')}
          onExportError={() => showToast('Failed to export data. Please try again.', 'error')}
        />

        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}

        <div className="trust-note">
          <div className="trust-note-text">
            <span className="trust-note-icon">✔</span>
            Your data is securely stored and encrypted
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
